import React from "react";
import styled, { css, keyframes } from "styled-components";

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  LANDING_PHOTO_FADE_IN_MS,
  LANDING_PHOTO_FADE_OUT_MS,
  SHOWCASE_FADE_IN_MS,
  SHOWCASE_HOLD_MS,
  SHOWCASE_MS,
} from "../../constants";
import { rgbToHex } from "../../colorUtils";
import useSize from "../../hooks/useSize";
import { marcImageUrl } from "../../paths";
import { invertMarcTransform, marcTransformToCss } from "../../virtualCoords";
import Easel, {
  EASEL_WIDTH,
  EASEL_HEIGHT,
  EASEL_CANVAS_POSITION,
} from "../Easel";

// Thickness of the dark border drawn around the Frame. Used by both the
// CSS (Frame's border) and the Frame-sizing math below (to translate
// between the Frame's border box and the inner canvas-aspect content
// box), so it must stay in sync.
const FRAME_BORDER_PX = 8;

// Renders the reference photo on top of the SVG easel.
//
// Nesting, outside in:
//
//   Stage (EASEL aspect)   — easel's outer rectangle, aspect-locked
//   ├── Easel              — wooden easel SVG lines
//   └── CanvasArea         — rect on easel where painting sits; flex
//       │                    column, flex-end, so the Frame rests on
//       │                    the easel's tray when aspects don't match.
//       └── Frame          — explicit width/height (computed from
//                            useSize), dark border, orange bg. Photo
//                            sits directly inside at 100%×100%.
//           └── Photo (img, object-fit + marcTransform scaled by mult)
//
// Why we compute Frame dimensions in JS rather than CSS: we want the
// Frame's CONTENT box (inside the border) to have exactly canvas aspect
// so marcTransform's relative positioning transfers perfectly from the
// drawing side. CSS aspect-ratio + fixed-px border makes that difficult
// (aspect applies to border box; content box drifts). Measuring the
// CanvasArea, doing the math, and setting explicit dimensions is
// simpler and exact at every size.
function ReferencePhoto({
  marcId,
  marcTransform,
  // Debug-only: when true and `referenceDrawingSrc` is non-null, swap
  // the marc photo for the saved reference trace (data URL from
  // reference.json's drawing.image). The reference image is already
  // in canvas-virtual coords with no transform baked in, so we render
  // it without marcTransform — it lines up with the drawing canvas
  // at native scale and you can compare sizes by eye.
  referenceDrawingSrc,
  showReferenceDrawing,
  // Landing reel: fades the marc photo only (NOT the dark easel
  // frame, which stays put as a "frame on the easel" visual anchor
  // while photos swap inside it). Default true so non-landing modes
  // always render the photo at full opacity.
  photoVisible = true,
  // Landing-only safety net for the cloudflare flicker: when set,
  // hold the photo at opacity 0 until the current src has actually
  // finished loading. Without this, a cycle's fade-in can briefly
  // reveal the previous (still-rendered) image while the new src is
  // decoding, which on slow networks reads as the wrong marc fading
  // in. Off in non-landing modes — those tolerate the browser's
  // "render last frame until new decodes" snap on marcId change
  // since there's no fade to expose the artifact.
  photoLoadGate = false,
  // Speedrun-only: when set, the photo runs an inverse-opacity
  // keyframes animation in lockstep with the canvas marc overlay
  // (cross-fade out → hold at 0 → cross-fade in). The src/transform
  // swap to the next marc happens mid-animation (advanceMarc fires at
  // start of phase 3) while the easel is at opacity 0, so the
  // fade-back-in is the new marc's photo arriving.
  showcasing = false,
  // When false, drop the easel SVG + blob shadow; Stage is canvas-
  // aspect and the dark Frame border is the only chrome.
  withEasel = true,
  // When set, layer a click-capture overlay over the photo. Hover →
  // sample the natural-image pixel under the cursor and call
  // onPreviewPixel(hex). Click → onPickPixel(hex). marcTransform is
  // inverted so the user picks what they see, not where the
  // un-transformed image would be.
  eyedropperActive = false,
  onPickPixel,
  onPreviewPixel,
}) {
  const showDrawing = showReferenceDrawing && !!referenceDrawingSrc;
  const src = showDrawing ? referenceDrawingSrc : marcImageUrl(marcId);
  const [loadedSrc, setLoadedSrc] = React.useState(null);
  const photoReady = !photoLoadGate || loadedSrc === src;
  const canvasAreaRef = React.useRef(null);
  const canvasAreaSize = useSize(canvasAreaRef);
  const photoRef = React.useRef(null);

  // Fit a canvas-aspect rectangle inside the available space (CanvasArea
  // minus the border thickness on every side), then add the border back
  // to get the Frame's border-box dimensions. `mult` is the resulting
  // display scale: display px per virtual px.
  const innerBudget = FRAME_BORDER_PX * 2;
  const availWidth = canvasAreaSize.width - innerBudget;
  const availHeight = canvasAreaSize.height - innerBudget;
  const canvasAspect = CANVAS_WIDTH / CANVAS_HEIGHT;

  let frameInnerWidth = 0;
  let frameInnerHeight = 0;
  let mult = 1;
  if (availWidth / canvasAspect > availHeight) {
    frameInnerHeight = availHeight;
    frameInnerWidth = availHeight * canvasAspect;
    mult = frameInnerWidth / CANVAS_WIDTH;
  } else {
    frameInnerWidth = availWidth;
    frameInnerHeight = availWidth / canvasAspect;
    mult = frameInnerHeight / CANVAS_HEIGHT;
  }
  const frameWidth = frameInnerWidth + innerBudget;
  const frameHeight = frameInnerHeight + innerBudget;

  // Map a pointer event over the click overlay to the underlying
  // natural-image hex. Returns null if the cursor is outside the photo's
  // letterboxed fitted rectangle, or if the image isn't ready yet.
  const samplePixelAt = (e) => {
    const img = photoRef.current;
    if (!img) return null;
    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    if (!nW || !nH) return null;

    const rect = e.currentTarget.getBoundingClientRect();
    const dispX = e.clientX - rect.left;
    const dispY = e.clientY - rect.top;
    const virtX = dispX / mult;
    const virtY = dispY / mult;

    const layout = invertMarcTransform(
      virtX,
      virtY,
      marcTransform,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
    );

    // object-fit: contain letterboxes the natural image inside the
    // canvas-virtual layout box. Map layout coords → natural coords.
    const fitAspect = nW / nH;
    const layoutAspect = CANVAS_WIDTH / CANVAS_HEIGHT;
    let fittedW;
    let fittedH;
    let offsetX;
    let offsetY;
    if (fitAspect > layoutAspect) {
      fittedW = CANVAS_WIDTH;
      fittedH = CANVAS_WIDTH / fitAspect;
      offsetX = 0;
      offsetY = (CANVAS_HEIGHT - fittedH) / 2;
    } else {
      fittedH = CANVAS_HEIGHT;
      fittedW = CANVAS_HEIGHT * fitAspect;
      offsetX = (CANVAS_WIDTH - fittedW) / 2;
      offsetY = 0;
    }
    const natX = ((layout.x - offsetX) * nW) / fittedW;
    const natY = ((layout.y - offsetY) * nH) / fittedH;
    if (natX < 0 || natY < 0 || natX >= nW || natY >= nH) return null;

    const sampler = document.createElement("canvas");
    sampler.width = 1;
    sampler.height = 1;
    const ctx = sampler.getContext("2d", { willReadFrequently: false });
    try {
      ctx.drawImage(img, natX | 0, natY | 0, 1, 1, 0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      if (a === 0) return null;
      return rgbToHex(r, g, b);
    } catch {
      // Cross-origin taint; nothing we can do without CORS on the asset.
      return null;
    }
  };

  const handleEyedropperMove = (e) => {
    if (!onPreviewPixel) return;
    const hex = samplePixelAt(e);
    if (hex) onPreviewPixel(hex);
  };
  const handleEyedropperClick = (e) => {
    if (!onPickPixel) return;
    const hex = samplePixelAt(e);
    if (hex) onPickPixel(hex);
  };

  // Gate the Stage on having a real measurement — Frame's width/height
  // come from useSize, which starts at 0 and lands a tick later. Without
  // this, the easel briefly renders with a 0×0 Frame inside before the
  // photo pops in at its real size.
  const measured = canvasAreaSize.width > 0 && canvasAreaSize.height > 0;

  return (
    <Stage
      $withEasel={withEasel}
      style={{ visibility: measured ? "visible" : "hidden" }}
    >
      {withEasel && <Easel />}
      <CanvasArea
        ref={canvasAreaRef}
        style={
          withEasel
            ? EASEL_CANVAS_POSITION
            : { left: 0, top: 0, width: "100%", height: "100%" }
        }
      >
        <Frame style={{ width: frameWidth, height: frameHeight }}>
          <Photo
            ref={photoRef}
            src={src}
            alt={`Marc Andreessen (${marcId})`}
            draggable={false}
            onLoad={() => setLoadedSrc(src)}
            onError={() => setLoadedSrc(src)}
            $showcasing={showcasing}
            style={{
              // Photo is positioned left:50%/top:50%; the centering
              // translate(-50%, -50%) lives inside marcTransformToCss.
              // For the reference drawing we render with no marc
              // transform but still need that centering translate
              // (otherwise the image lands in the bottom-right
              // quadrant of the frame).
              transform: showDrawing
                ? "translate(-50%, -50%)"
                : marcTransformToCss(marcTransform, mult),
              opacity: photoVisible && photoReady ? 1 : 0,
              // Opacity-only transition. The transform jumps when marcId
              // swaps — that used to be tweened over LANDING_EGG_MOVE_MS,
              // but the photo is at opacity 0 during every marc swap (the
              // landing cycle hides it during photo-fade-out, the speedrun
              // showcase keyframes holds it at 0 across phase 2), so the
              // tween was always running invisibly anyway. Dropping it
              // means the new marc's photo arrives already at its final
              // pose when it fades back in.
              //
              // While `showcasing`, the styled-component layers a CSS
              // animation that drives opacity directly — animation
              // overrides inline opacity / opacity-transition for its
              // duration, so this inline opacity only governs the
              // non-showcase states.
              transition: `opacity ${
                photoVisible
                  ? LANDING_PHOTO_FADE_IN_MS
                  : LANDING_PHOTO_FADE_OUT_MS
              }ms ease-out`,
            }}
          />
          {eyedropperActive && (
            <PickOverlay
              onClick={handleEyedropperClick}
              onPointerMove={handleEyedropperMove}
            />
          )}
        </Frame>
      </CanvasArea>
    </Stage>
  );
}

// Outer rectangle. Aspect tracks easel-or-canvas; cqw/cqh require the
// parent to declare `container-type: size`.
const Stage = styled.div`
  position: relative;
  aspect-ratio: ${(p) =>
    p.$withEasel
      ? `${EASEL_WIDTH} / ${EASEL_HEIGHT}`
      : `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`};
  width: ${(p) =>
    p.$withEasel
      ? `min(100cqw, calc(100cqh * ${EASEL_WIDTH} / ${EASEL_HEIGHT}))`
      : `min(100cqw, calc(100cqh * ${CANVAS_WIDTH} / ${CANVAS_HEIGHT}))`};

  // Floor shadow only when there's an easel to anchor.
  ${(p) =>
    p.$withEasel &&
    css`
      &::after {
        content: "";
        position: absolute;
        background: radial-gradient(
          ellipse at center,
          rgba(0, 0, 0, 0.2) 0%,
          rgba(0, 0, 0, 0.1) 60%,
          transparent 100%
        );
        bottom: 0;
        width: 85%;
        border-radius: 50%;
        left: 50%;
        transform: translate(-50%, 23%);
        height: 16%;
        filter: blur(14px);
      }
    `}
`;

// CanvasArea: the rectangle on the easel where the painting sits.
// Positioned by the consumer via the style prop (EASEL_CANVAS_POSITION
// from Easel.jsx). Flex column with justify-content: flex-end so the
// Frame rests on the bottom of the canvas-area — when the Frame's aspect
// doesn't exactly match the easel canvas-area's aspect, the leftover
// space ends up above the Frame instead of below it, preserving the
// visual sense of the painting sitting on the tray.
const CanvasArea = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

// Frame: the visible dark-bordered rectangle around Marc. Width/height
// are computed in JS from useSize (see component body); border is
// FRAME_BORDER_PX on every side, so the content box has canvas aspect
// exactly. Photo sits inside at 100%×100%.
const Frame = styled.div`
  background-color: var(--canvas-bg);
  border: ${FRAME_BORDER_PX}px solid var(--color-chrome);
  box-shadow:
    0 0 1px 2px rgba(0, 0, 0, 0.25) inset,
    0 0 1px 1px var(--color-gold-shadow);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
`;

// Easel cross-fade for marc-completion showcase. Inverse of the canvas
// marc overlay's opacity keyframes — starts visible, fades to 0 over
// phase 1, holds invisible, fades back to 1 over phase 3. The src/
// transform swap to the next marc happens during the hold (advanceMarc
// fires at phase 3 start) so the fade-back-in is the new marc arriving.
// Phase percentages match DrawingArea's showcaseMarcKeyframes exactly,
// keeping the two fades in lockstep.
const EASEL_FADE_IN_PCT =
  (SHOWCASE_FADE_IN_MS / SHOWCASE_MS) * 100;
const EASEL_HOLD_END_PCT =
  ((SHOWCASE_FADE_IN_MS + SHOWCASE_HOLD_MS) / SHOWCASE_MS) * 100;
const easelShowcaseKeyframes = keyframes`
  0% {
    opacity: 1;
  }
  ${EASEL_FADE_IN_PCT}% {
    opacity: 0;
  }
  ${EASEL_HOLD_END_PCT}% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Photo: the Marc image inside Frame. width/height: 100% means the
// layout box equals Frame's content box (already at display size).
// `transform` applies marcTransform via marcTransformToCss(mult=display
// scale) so tx/ty (stored in virtual px) are rendered as display px.
// object-fit: contain preserves the natural photo aspect within the
// layout box; letterbox strips show Frame's orange bg.
//
// While $showcasing, the keyframes animation drives opacity directly —
// CSS animation overrides inline opacity + opacity-transition for the
// duration of its play. Once $showcasing flips back to false (at the end
// of SHOWCASE_MS), the animation rule disappears and the element returns
// to its inline opacity (1, since by that point the new-marc fade-in has
// already ended at opacity 1, matching).
const Photo = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
  ${(p) =>
    p.$showcasing &&
    css`
      animation: ${easelShowcaseKeyframes} ${SHOWCASE_MS}ms ease-out forwards;
    `}
`;

const PickOverlay = styled.div`
  position: absolute;
  inset: 0;
  cursor: crosshair;
  z-index: 5;
`;

export default ReferencePhoto;
